import { lineStrings } from 'constants/strings';
import {
  CARD_TYPE_PREVIEW_HEIGHT,
  CARD_TYPE_PREVIEW_IMAGE_HEIGHT,
  getAdditionalInfoPreviewIcon,
} from 'constants/cardTypeMessageTemplates';
import { getCardTypeTagColor } from 'constants/cardTypeTagColors';
import {
  ECardTypeTemplateId,
  TCardTypeCard,
  TCardTypePrice,
  TCardTypeTag,
} from 'types/cardTypeMessage';
import { getCardImages } from 'utils/cardTypeMessage';
import { CardTypeMessageImageGridPreview } from './CardTypeMessageImageGrid';

const strings = lineStrings.management.cardTypeMessage;

/** 2項目目の価格行の高さぶん、固定高さのプレビューを伸ばして切れを防ぐ */
const CARD_TYPE_PREVIEW_PRICE_ROW_HEIGHT = 24;

type Props = {
  templateId: ECardTypeTemplateId;
  card: TCardTypeCard;
  variant?: 'editor' | 'inline';
  lazyImages?: boolean;
};

function TagPill({ tag }: { tag: TCardTypeTag }) {
  if (!tag.enabled) return null;
  const text = tag.text.trim() || strings.enterTagText;
  const color = getCardTypeTagColor(tag.color);
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ backgroundColor: color.backgroundColor, color: color.textColor }}
    >
      {text}
    </span>
  );
}

export function CardTypeMessagePreview({
  templateId,
  card,
  variant = 'editor',
  lazyImages = false,
}: Props) {
  const isInline = variant === 'inline';
  const images = getCardImages(card);
  const imageCount = (card.imageCount ?? 1) as 1 | 2 | 3;
  const previewActions = card.actions.filter((a) => a.enabled);

  /** 1つ目は従来どおり（価格未定義の既定カードもプレースホルダー表示）、2つ目は有効時のみ */
  const previewPrices: TCardTypePrice[] =
    templateId === ECardTypeTemplateId.Products
      ? [
          ...(card.price?.enabled !== false
            ? [
                card.price ?? {
                  enabled: true,
                  label: '',
                  currency: '',
                  value: '',
                },
              ]
            : []),
          ...(card.price2?.enabled ? [card.price2] : []),
        ]
      : [];

  const shellStyle = isInline
    ? undefined
    : ({
        height:
          CARD_TYPE_PREVIEW_HEIGHT +
          (previewPrices.length > 1 ? CARD_TYPE_PREVIEW_PRICE_ROW_HEIGHT : 0),
        minHeight:
          CARD_TYPE_PREVIEW_HEIGHT +
          (previewPrices.length > 1 ? CARD_TYPE_PREVIEW_PRICE_ROW_HEIGHT : 0),
      } as const);

  const shellClass =
    'box-border flex shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-white shadow-sm';
  const inlineShellClass = `${shellClass} w-full max-w-[220px]`;
  const outerClass = isInline
    ? inlineShellClass
    : `${shellClass} w-full max-w-[282px]`;

  if (templateId === ECardTypeTemplateId.Pictures) {
    const primaryAction = previewActions[0];
    const picturesClass = isInline
      ? `${shellClass} aspect-square w-full`
      : `${shellClass} aspect-square w-full max-w-[282px]`;

    return (
      <div data-testid="card-preview-shell" className={picturesClass}>
        <div className="relative aspect-square w-full overflow-hidden bg-neutral-100">
          {card.tag?.enabled ? (
            <div className="absolute left-2 top-2 z-10">
              <TagPill tag={card.tag} />
            </div>
          ) : null}
          {images.a ? (
            <img
              src={images.a}
              alt=""
              data-testid="card-preview-image"
              className="h-full w-full object-cover"
              loading={lazyImages ? 'lazy' : undefined}
              decoding={lazyImages ? 'async' : undefined}
            />
          ) : (
            <CardTypeMessageImageGridPreview
              imageCount={1}
              images={images}
              square
              className="h-full"
              lazyImages={lazyImages}
            />
          )}
          {primaryAction ? (
            <div
              data-testid="card-preview-action"
              className={`absolute inset-x-0 bottom-4 z-10 flex justify-center ${
                isInline ? 'px-2' : 'px-3'
              }`}
            >
              <span
                className={`inline-block max-w-full rounded-[24px] bg-black/40 text-center font-semibold text-white ${
                  isInline
                    ? 'break-words px-2 py-2 text-[10px]'
                    : 'break-words px-4 py-3 text-xs'
                }`}
              >
                {primaryAction.label.trim() || strings.enterActionLabel}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (templateId === ECardTypeTemplateId.People) {
    const enabledTags = card.tags?.filter((tag) => tag.enabled !== false) ?? [];
    const tagRows: (typeof enabledTags)[] = [];
    for (let i = 0; i < enabledTags.length; i += 2) {
      tagRows.push(enabledTags.slice(i, i + 2));
    }

    return (
      <div data-testid="card-preview-shell" className={outerClass} style={shellStyle}>
        <div className="flex min-h-0 flex-1 flex-col items-center bg-white px-4 pb-3 pt-6">
          <div className="mb-4 h-[104px] w-[104px] shrink-0 overflow-hidden rounded-full bg-neutral-200">
            {images.a ? (
              <img
                src={images.a}
                alt=""
                data-testid="card-preview-image"
                className="h-full w-full object-cover"
                loading={lazyImages ? 'lazy' : undefined}
                decoding={lazyImages ? 'async' : undefined}
              />
            ) : null}
          </div>
          <p className="text-center text-base font-bold leading-tight text-ink">
            {card.name?.trim() || strings.enterName}
          </p>
          {tagRows.length > 0 ? (
            <div className="mt-3 flex w-full flex-col items-center gap-1.5">
              {tagRows.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className="flex flex-wrap justify-center gap-1.5"
                >
                  {row.map((tag, i) => (
                    <TagPill
                      key={`${rowIndex}-${i}`}
                      tag={tag}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : null}
          {card.description?.enabled !== false ? (
            <p className="mt-3 w-full text-left text-xs leading-relaxed text-[#888888] line-clamp-3">
              {card.description?.text.trim() || strings.enterDescription}
            </p>
          ) : null}
        </div>
        {previewActions.length > 0 ? (
          <div className="shrink-0">
            {previewActions.map((action, i) => (
              <div
                key={i}
                className="px-3 py-2.5 text-center text-xs font-medium text-blue-500"
              >
                {action.label.trim() || strings.enterActionLabel}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  const useMultiImage =
    templateId === ECardTypeTemplateId.Products ||
    templateId === ECardTypeTemplateId.Places;

  return (
    <div data-testid="card-preview-shell" className={outerClass} style={shellStyle}>
      <div className="relative shrink-0">
        {card.tag?.enabled !== false ? (
          <div className="absolute left-2 top-2 z-10">
            <TagPill tag={card.tag!} />
          </div>
        ) : null}
        {useMultiImage ? (
          <CardTypeMessageImageGridPreview
            imageCount={imageCount}
            images={images}
            lazyImages={lazyImages}
          />
        ) : images.a ? (
          <div
            className="w-full overflow-hidden"
            style={{ height: CARD_TYPE_PREVIEW_IMAGE_HEIGHT }}
          >
            <img
              src={images.a}
              alt=""
              data-testid="card-preview-image"
              className="h-full w-full object-cover"
              loading={lazyImages ? 'lazy' : undefined}
              decoding={lazyImages ? 'async' : undefined}
            />
          </div>
        ) : (
          <CardTypeMessageImageGridPreview
            imageCount={1}
            images={images}
            lazyImages={lazyImages}
          />
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
        <div className="min-h-0 overflow-hidden">
          <p className="text-sm font-bold text-ink">
            {card.header?.trim() || strings.enterHeader}
          </p>
          {templateId === ECardTypeTemplateId.Places &&
          card.address?.enabled !== false ? (
            <p className="mt-1 flex items-start gap-1 text-xs text-muted">
              <span>📍</span>
              <span>{card.address?.text.trim() || strings.enterAddress}</span>
            </p>
          ) : null}
          {templateId === ECardTypeTemplateId.Places &&
          card.additionalInfo?.enabled !== false ? (
            <p className="mt-1 flex items-start gap-1 text-xs text-muted">
              <span className="shrink-0 leading-none">
                {getAdditionalInfoPreviewIcon(card.additionalInfo?.infoType)}
              </span>
              <span>
                {card.additionalInfo?.text.trim() || strings.enterInfoDetails}
              </span>
            </p>
          ) : null}
          {templateId === ECardTypeTemplateId.Products &&
          card.description?.enabled !== false ? (
            <p className="mt-1 text-xs text-muted line-clamp-2">
              {card.description?.text.trim() || strings.enterDescription}
            </p>
          ) : null}
        </div>
        {previewPrices.length > 0 ? (
          <div data-testid="card-preview-prices" className="mt-2 shrink-0 space-y-0.5">
            {previewPrices.map((price, index) => (
              <p
                key={index}
                data-testid="card-preview-price-row"
                className="flex items-baseline justify-between gap-2 text-sm font-semibold text-ink"
              >
                {(price.label ?? '').trim() ? (
                  <span
                    data-testid="card-preview-price-label"
                    className="min-w-0 break-words text-left text-xs font-normal text-muted"
                  >
                    {(price.label ?? '').trim()}
                  </span>
                ) : null}
                <span
                  data-testid="card-preview-price-amount"
                  className="ml-auto whitespace-nowrap"
                >
                  {price.currency}
                  {price.value.trim() || '000.00'}
                </span>
              </p>
            ))}
          </div>
        ) : null}
      </div>
      {previewActions.length > 0 ? (
        <div className="shrink-0">
          {previewActions.map((action, i) => (
            <div
              key={i}
              className="px-3 py-2.5 text-center text-xs font-medium text-blue-500"
            >
              {action.label.trim() || strings.enterActionLabel}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
